import { forwardRef, useRef, createContext, useContext, useEffect } from 'react';
import { useComposedRefs } from '@usertour/react-compose-refs';
import { autoUpdate, ReferenceElement } from '@floating-ui/dom';
import { useFloating, offset, shift, limitShift, flip, size } from '@floating-ui/react-dom';
import type { Placement } from '@floating-ui/dom';
import { createCustomHideMiddleware } from './utils/floating-ui-middleware';
import { createPlatformWithDetachedReferenceFallback } from './utils/floating-ui-platform';
import type { Rect } from '@floating-ui/dom';
import { getRegisteredIconNames, getIcon } from '@usertour/icons';
import {
  Align,
  LauncherData,
  LauncherDataType,
  LauncherIconSource,
  Side,
  ThemeTypesSetting,
} from '@usertour/types';
import { cn } from '@usertour/tailwind';
import {
  Popper,
  PopperContent,
  PopperContentPotal,
  PopperContentProps,
  PopperProps,
} from './popper';
import { useLauncherPopperContentProps } from './hooks/use-launcher-popper-content-props';
import { useSettingsStyles } from './hooks/use-settings-styles';
import { useWidgetLocale } from '../locale/context';
import { hiddenStyle } from './utils/content';
import { WidgetClass } from './class-names';

function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}

type Boundary = Element | null;

/**
 * Convert icon name to display text
 * e.g., 'home-line' -> 'Home Line', 'chat1-fill' -> 'Chat1 Fill'
 */
const formatIconName = (name: string): string => {
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generate IconsList from registered RemixIcon icons
 * Includes all registered RemixIcon icons
 */
const generateIconsList = () => {
  const registeredNames = getRegisteredIconNames();
  const icons = registeredNames
    .map((name) => {
      const icon = getIcon(name);
      if (!icon) {
        return null;
      }
      return {
        ICON: icon,
        text: formatIconName(name),
        name,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Sort icons by name for better UX
  icons.sort((a, b) => a.name.localeCompare(b.name));

  return icons;
};

export const IconsList = generateIconsList();

interface LauncherContentProps {
  type?: LauncherDataType;
  onOpenChange?: (open: boolean) => void;
  onSizeChange?: (rect: { width: number; height: number }) => void;
  side?: Side;
  sideOffset?: number;
  align?: Align;
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionBoundary?: Boundary | Boundary[];
  collisionPadding?: number | Partial<Record<Side, number>>;
  sticky?: 'partial' | 'always';
  hideWhenDetached?: boolean;
  dir?: string;
  globalStyle?: string;
  updatePositionStrategy?: 'optimized' | 'always';
  referenceRef?: React.RefObject<any>;
  iconType?: string;
  iconSource?: LauncherIconSource;
  iconUrl?: string;
  buttonText?: string;
  zIndex: number;
}

interface LauncherRootProps {
  children: React.ReactNode;
  themeSettings: ThemeTypesSetting;
  data: LauncherData;
}

interface LauncherContextValue {
  globalStyle: string;
  themeSetting?: ThemeTypesSetting;
  data: LauncherData;
}

const LauncherContext = createContext<LauncherContextValue | null>(null);

const useLauncherContext = () => {
  const context = useContext(LauncherContext);
  if (!context) {
    throw new Error('useLauncherContext must be used within a LauncherRoot');
  }
  return context;
};

const LauncherRoot = (props: LauncherRootProps) => {
  const { children, themeSettings, data } = props;
  const { globalStyle, themeSetting } = useSettingsStyles(themeSettings);

  return (
    <LauncherContext.Provider value={{ globalStyle, themeSetting, data }}>
      {children}
    </LauncherContext.Provider>
  );
};

LauncherRoot.displayName = 'LauncherRoot';

interface LauncherContainerProps {
  children: React.ReactNode;
}

const LauncherContainer = forwardRef<HTMLDivElement, LauncherContainerProps>(
  ({ children }, ref) => {
    const { globalStyle } = useLauncherContext();
    const composedRefs = useComposedRefs(ref, (el: HTMLDivElement | null) => {
      if (el?.style) {
        el.style.cssText = globalStyle;
      }
    });
    return <div ref={composedRefs}>{children}</div>;
  },
);

LauncherContainer.displayName = 'LauncherContainer';

// Add interfaces before the component definitions
interface LauncherIconProps {
  type: LauncherDataType;
  iconType?: string;
  iconSource?: LauncherIconSource;
  iconUrl?: string;
  width?: number;
  height?: number;
}

// Simple icon preview component for uploaded/URL icons
const IconPreview = ({
  iconUrl,
  size,
}: {
  iconUrl: string;
  size: number;
}) => {
  const { messages } = useWidgetLocale();
  return (
    <img
      src={iconUrl}
      alt={messages.customIcon}
      width={size}
      height={size}
      style={{ objectFit: 'contain' }}
    />
  );
};

// UI Components
const LauncherIcon = ({
  type,
  iconType,
  iconSource,
  iconUrl,
  width,
  height,
}: LauncherIconProps) => {
  const iconSize = width ?? height ?? 24;

  if (type === LauncherDataType.BEACON) {
    return (
      <>
        <div className={WidgetClass.beaconHalo} />
        <div className={WidgetClass.beaconCore} />
      </>
    );
  }

  if (type === LauncherDataType.ICON) {
    // Handle uploaded or URL icons
    if (
      (iconSource === LauncherIconSource.UPLOAD || iconSource === LauncherIconSource.URL) &&
      iconUrl
    ) {
      return <IconPreview iconUrl={iconUrl} size={iconSize} />;
    }

    // Handle built-in icons
    const ActiveIcon = IconsList.find((item) => item.name === iconType)?.ICON;
    if (ActiveIcon) {
      return <ActiveIcon size={iconSize} />;
    }
  }

  return null;
};

LauncherIcon.displayName = 'LauncherIcon';

interface LauncherViewProps {
  className?: string;
  style: React.CSSProperties;
  dir?: string;
  type: LauncherDataType;
  iconType?: string;
  iconSource?: LauncherIconSource;
  iconUrl?: string;
  buttonText?: string;
}

const LauncherView = forwardRef<HTMLDivElement, LauncherViewProps>(
  ({ className, style, dir, type, iconType, iconSource, iconUrl, buttonText }, ref) => {
    const { themeSetting } = useLauncherContext();
    const isClick = true;

    let classes = 'usertour-widget-launcher';

    if (type === LauncherDataType.BUTTON) {
      classes = `${classes} usertour-widget-launcher--button`;
    } else if (type === LauncherDataType.BEACON) {
      classes = `${classes} usertour-widget-beacon`;
    } else {
      classes = `${classes} usertour-widget-launcher--icon`;
    }

    if (isClick) {
      classes = `${classes} usertour-widget-launcher--activate-on-click`;
    }

    return (
      <div className={cn(classes, className)} ref={ref} style={style} dir={dir}>
        {type === LauncherDataType.BUTTON ? (
          buttonText
        ) : (
          <LauncherIcon
            type={type}
            iconType={iconType}
            iconSource={iconSource}
            iconUrl={iconUrl}
            width={themeSetting?.launcherIcon.size}
            height={themeSetting?.launcherIcon.size}
          />
        )}
      </div>
    );
  },
);

LauncherView.displayName = 'LauncherView';

const LauncherContent = forwardRef<HTMLDivElement, LauncherContentProps>((props, forwardedRef) => {
  const {
    side = 'bottom',
    sideOffset = 0,
    align = 'center',
    alignOffset = 0,
    avoidCollisions = true,
    collisionBoundary = [],
    collisionPadding: collisionPaddingProp = 0,
    sticky = 'partial',
    hideWhenDetached = false,
    dir = 'ltr',
    updatePositionStrategy = 'optimized',
    referenceRef,
    zIndex,
    type = LauncherDataType.ICON,
    iconType,
    iconSource,
    iconUrl,
    buttonText,
  } = props;

  const referenceEl = referenceRef?.current as ReferenceElement;
  const lastReferenceRectRef = useRef<Rect | null>(null);

  // Clear cached rect when the reference element changes so we never use a previous rect.
  useEffect(() => {
    lastReferenceRectRef.current = null;
  }, [referenceEl]);

  const { globalStyle } = useLauncherContext();

  const desiredPlacement = `${side}${align !== 'center' ? `-${align}` : ''}` as Placement;
  const collisionPadding =
    typeof collisionPaddingProp === 'number'
      ? collisionPaddingProp
      : { top: 0, right: 0, bottom: 0, left: 0, ...collisionPaddingProp };

  const boundary = Array.isArray(collisionBoundary) ? collisionBoundary : [collisionBoundary];
  const hasExplicitBoundaries = boundary.length > 0;

  const detectOverflowOptions = {
    padding: collisionPadding,
    boundary: boundary.filter(isNotNull),
    // with `strategy: 'fixed'`, this is the only way to get it to respect boundaries
    altBoundary: hasExplicitBoundaries,
  };

  const { refs, floatingStyles, isPositioned, middlewareData } = useFloating({
    // default to `fixed` strategy so users don't have to pick and we also avoid focus scroll issues
    strategy: 'fixed',
    placement: desiredPlacement,
    whileElementsMounted: (...args) => {
      const cleanup = autoUpdate(...args, {
        animationFrame: updatePositionStrategy === 'always',
      });
      return cleanup;
    },
    elements: {
      reference: referenceEl,
    },
    platform: createPlatformWithDetachedReferenceFallback(lastReferenceRectRef),
    middleware: [
      offset({
        mainAxis: sideOffset,
        alignmentAxis: alignOffset,
      }),
      avoidCollisions &&
        shift({
          mainAxis: true,
          crossAxis: false,
          limiter: sticky === 'partial' ? limitShift() : undefined,
          ...detectOverflowOptions,
        }),
      avoidCollisions && flip({ ...detectOverflowOptions }),
      size({
        ...detectOverflowOptions,
      }),
      hideWhenDetached && createCustomHideMiddleware(detectOverflowOptions),
    ],
  });

  const popperRef = useRef<HTMLDivElement | null>(null);
  const composedRefs = useComposedRefs(forwardedRef, popperRef, (node: any) =>
    refs.setFloating(node),
  );

  function parseStyleString(styleString: string): Record<string, string> {
    // Initialize result object
    const result: Record<string, string> = {};

    // Split by semicolon and filter out empty strings
    const declarations = styleString.split(';').filter((declaration) => declaration.trim());

    for (const declaration of declarations) {
      // Find the first colon to separate property and value
      const colonIndex = declaration.indexOf(':');
      if (colonIndex === -1) continue;

      const property = declaration.substring(0, colonIndex).trim();
      const value = declaration.substring(colonIndex + 1).trim();

      if (property && value) {
        result[property] = value;
      }
    }

    return result;
  }

  const combinedStyles = {
    ...parseStyleString(globalStyle),
    ...floatingStyles,
    zIndex,
    transform: isPositioned ? floatingStyles.transform : 'translate(0, -200%)',
    // Pre-positioning hide must NOT use opacity: this element is also the
    // .usertour-widget-launcher--icon, whose theme opacity lives in a class
    // (`opacity: var(--usertour-widget-launcher-icon-opacity)`). An inline
    // opacity here would override it and the theme transparency would never
    // apply. Hide with visibility instead (matches popper.tsx's hide).
    visibility: (isPositioned ? 'visible' : 'hidden') as 'visible' | 'hidden',
    ...(middlewareData.customHide?.referenceHidden ? hiddenStyle : {}),
  };

  return (
    <LauncherView
      ref={composedRefs}
      style={combinedStyles}
      dir={dir}
      type={type}
      iconType={iconType}
      iconSource={iconSource}
      iconUrl={iconUrl}
      buttonText={buttonText}
    />
  );
});

LauncherContent.displayName = 'LauncherContent';

const LauncherPopper = forwardRef<HTMLDivElement, Omit<PopperProps, 'globalStyle'>>(
  (props, ref) => {
    const { globalStyle } = useLauncherContext();
    return <Popper ref={ref} {...props} globalStyle={globalStyle} />;
  },
);

LauncherPopper.displayName = 'LauncherPopper';

const LauncherPopperContentPotal = forwardRef<HTMLDivElement, PopperContentProps>((props, ref) => {
  const { themeSetting, data } = useLauncherContext();
  const popperContentProps = useLauncherPopperContentProps({ themeSetting, data });

  return <PopperContentPotal ref={ref} {...popperContentProps} {...props} />;
});

LauncherPopperContentPotal.displayName = 'LauncherPopperContentPotal';

const LauncherPopperContent = PopperContent;

const LauncherContentWrapper = forwardRef<HTMLDivElement, LauncherContentProps>(
  ({ ...props }, ref) => {
    const { data } = useLauncherContext();
    const {
      side = 'bottom',
      align,
      sideOffset = 0,
      alignOffset = 0,
      alignType = 'auto',
    } = data.target.alignment;

    return (
      <LauncherContent
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        align={alignType === 'auto' ? 'center' : (align ?? 'center')}
        avoidCollisions={alignType === 'auto'}
        type={data.type}
        iconType={data.iconType}
        iconSource={data.iconSource}
        iconUrl={data.iconUrl}
        buttonText={data.buttonText}
        ref={ref}
        {...props}
      />
    );
  },
);

LauncherContentWrapper.displayName = 'LauncherContentWrapper';

export {
  LauncherRoot,
  LauncherContent,
  LauncherContentWrapper,
  LauncherIcon,
  LauncherView,
  LauncherPopper,
  LauncherPopperContent,
  LauncherPopperContentPotal,
  LauncherContainer,
};
